
export interface VehicleInfo {
  year: string;
  make: string;
  model: string;
  trim?: string;
  bodyStyle?: string;
  driveType?: string;
  fuelType?: string;
  msrp?: string;
  styleCount?: number;
  hasMultipleStyles?: boolean;
}

// Export the DataOneUtils object which contains all utility functions
export const DataOneUtils = {
  getVehicleInfo(data: any): VehicleInfo | null {
    if (!data) return null;
    
    try {
      // Check if query_responses exist and get the first response
      const queryResponses = data.query_responses;
      if (!queryResponses) return null;
      
      const firstResponseKey = Object.keys(queryResponses)[0];
      if (!firstResponseKey) return null;
      
      const response = queryResponses[firstResponseKey];
      
      // Check if using standard structure
      if (response?.styles?.style) {
        const style = Array.isArray(response.styles.style) 
          ? response.styles.style[0] 
          : response.styles.style;
        
        return {
          year: style.year || 'Unknown',
          make: style.division || 'Unknown',
          model: style.model || 'Unknown',
          trim: style.name || undefined,
          bodyStyle: style.body_type || undefined,
          driveType: style.drive_type || undefined,
          fuelType: style.fuel_type || undefined,
          msrp: style.msrp ? `$${style.msrp}` : undefined,
          styleCount: Array.isArray(response.styles.style) ? response.styles.style.length : 1,
          hasMultipleStyles: Array.isArray(response.styles.style) && response.styles.style.length > 1
        };
      }
      
      // Check if using US market data structure
      if (response?.us_market_data?.us_styles) {
        const usStyle = response.us_market_data.us_styles[0];
        if (!usStyle) return null;
        
        return {
          year: usStyle.year || data.vehicle?.year || 'Unknown',
          make: usStyle.make || data.vehicle?.make || 'Unknown',
          model: usStyle.model || data.vehicle?.model || 'Unknown',
          trim: usStyle.trim || undefined,
          bodyStyle: usStyle.body_style || undefined,
          driveType: usStyle.drive_type || undefined,
          fuelType: usStyle.fuel_type || undefined,
          styleCount: response.us_market_data.us_styles.length,
          hasMultipleStyles: response.us_market_data.us_styles.length > 1
        };
      }
      
      // Fallback to vehicle field if available
      if (data.vehicle) {
        return {
          year: data.vehicle.year || 'Unknown',
          make: data.vehicle.make || 'Unknown',
          model: data.vehicle.model || 'Unknown',
          trim: data.vehicle.trim || undefined
        };
      }
      
      return null;
    } catch (error) {
      console.error("Error extracting vehicle info:", error);
      return null;
    }
  },

  getAdasSystems(data: any): any[] {
    if (!data) return [];
    
    try {
      // Try to extract from standard structure
      const queryResponses = data.query_responses;
      if (!queryResponses) return [];
      
      const firstResponseKey = Object.keys(queryResponses)[0];
      if (!firstResponseKey) return [];
      
      const response = queryResponses[firstResponseKey];
      
      // Check if ADAS data exists in standard location
      if (response?.adas_features?.adas_system) {
        return Array.isArray(response.adas_features.adas_system)
          ? response.adas_features.adas_system
          : [response.adas_features.adas_system];
      }
      
      // Check for alternative locations
      if (response?.us_market_data?.us_styles) {
        const usStyle = response.us_market_data.us_styles[0];
        if (usStyle?.adas_features?.adas_system) {
          return Array.isArray(usStyle.adas_features.adas_system)
            ? usStyle.adas_features.adas_system
            : [usStyle.adas_features.adas_system];
        }
      }
      
      return [];
    } catch (error) {
      console.error("Error extracting ADAS systems:", error);
      return [];
    }
  },

  getEquipmentGroups(data: any): any[] {
    if (!data) return [];
    
    try {
      const queryResponses = data.query_responses;
      if (!queryResponses) return [];
      
      const firstResponseKey = Object.keys(queryResponses)[0];
      if (!firstResponseKey) return [];
      
      const response = queryResponses[firstResponseKey];
      
      // Check if equipment data exists in standard location
      if (response?.equipment?.equipment_group) {
        return Array.isArray(response.equipment.equipment_group)
          ? response.equipment.equipment_group
          : [response.equipment.equipment_group];
      }
      
      // Check for alternative locations
      if (response?.us_market_data?.us_styles) {
        const usStyle = response.us_market_data.us_styles[0];
        if (usStyle?.equipment?.equipment_group) {
          return Array.isArray(usStyle.equipment.equipment_group)
            ? usStyle.equipment.equipment_group
            : [usStyle.equipment.equipment_group];
        }
      }
      
      return [];
    } catch (error) {
      console.error("Error extracting equipment groups:", error);
      return [];
    }
  },

  getColorOptions(data: any): any {
    if (!data) return { exteriorColors: [], interiorColors: [] };
    
    try {
      const queryResponses = data.query_responses;
      if (!queryResponses) return { exteriorColors: [], interiorColors: [] };
      
      const firstResponseKey = Object.keys(queryResponses)[0];
      if (!firstResponseKey) return { exteriorColors: [], interiorColors: [] };
      
      const response = queryResponses[firstResponseKey];
      
      // Check if colors exist in standard location
      const exteriorColors = [];
      const interiorColors = [];
      
      if (response?.colors?.exterior_colors?.color) {
        const extColors = Array.isArray(response.colors.exterior_colors.color)
          ? response.colors.exterior_colors.color
          : [response.colors.exterior_colors.color];
        exteriorColors.push(...extColors);
      }
      
      if (response?.colors?.interior_colors?.color) {
        const intColors = Array.isArray(response.colors.interior_colors.color)
          ? response.colors.interior_colors.color
          : [response.colors.interior_colors.color];
        interiorColors.push(...intColors);
      }
      
      // Check for alternative locations
      if (response?.us_market_data?.us_styles) {
        const usStyle = response.us_market_data.us_styles[0];
        
        if (usStyle?.colors?.exterior_colors?.color) {
          const extColors = Array.isArray(usStyle.colors.exterior_colors.color)
            ? usStyle.colors.exterior_colors.color
            : [usStyle.colors.exterior_colors.color];
          exteriorColors.push(...extColors);
        }
        
        if (usStyle?.colors?.interior_colors?.color) {
          const intColors = Array.isArray(usStyle.colors.interior_colors.color)
            ? usStyle.colors.interior_colors.color
            : [usStyle.colors.interior_colors.color];
          interiorColors.push(...intColors);
        }
      }
      
      return { exteriorColors, interiorColors };
    } catch (error) {
      console.error("Error extracting color options:", error);
      return { exteriorColors: [], interiorColors: [] };
    }
  },

  getVehicleImages(data: any): any[] {
    if (!data) return [];
    
    try {
      const queryResponses = data.query_responses;
      if (!queryResponses) return [];
      
      const firstResponseKey = Object.keys(queryResponses)[0];
      if (!firstResponseKey) return [];
      
      const response = queryResponses[firstResponseKey];
      
      // Check if images exist in standard location
      if (response?.images?.image) {
        return Array.isArray(response.images.image)
          ? response.images.image
          : [response.images.image];
      }
      
      // Check for alternative locations
      if (response?.us_market_data?.us_styles) {
        const usStyle = response.us_market_data.us_styles[0];
        if (usStyle?.images?.image) {
          return Array.isArray(usStyle.images.image)
            ? usStyle.images.image
            : [usStyle.images.image];
        }
      }
      
      return [];
    } catch (error) {
      console.error("Error extracting vehicle images:", error);
      return [];
    }
  },

  // Add the missing helper functions that were referenced in the components
  
  // Alias functions to match the names used in the components
  extractAdasFeatures(data: any): any[] {
    return this.getAdasSystems(data);
  },

  extractColorsMaterials(data: any): any {
    return this.getColorOptions(data);
  },

  extractEquipmentFeatures(data: any): Record<string, any[]> {
    const equipmentGroups = this.getEquipmentGroups(data);
    if (!equipmentGroups || equipmentGroups.length === 0) return {};
    
    // Organize equipment by category
    const equipmentByCategory: Record<string, any[]> = {};
    
    equipmentGroups.forEach(group => {
      if (group?.equipment_category && group?.equipment_items?.equipment_item) {
        const category = group.equipment_category;
        const items = Array.isArray(group.equipment_items.equipment_item)
          ? group.equipment_items.equipment_item
          : [group.equipment_items.equipment_item];
        
        if (!equipmentByCategory[category]) {
          equipmentByCategory[category] = [];
        }
        
        equipmentByCategory[category].push(...items);
      }
    });
    
    return equipmentByCategory;
  },

  extractVehicleImages(data: any): any[] {
    return this.getVehicleImages(data);
  },

  // Group equipment by main categories
  groupEquipmentByCategory(equipment: Record<string, any[]>): Record<string, Record<string, any[]>> {
    if (!equipment || Object.keys(equipment).length === 0) return {};
    
    const grouped: Record<string, Record<string, any[]>> = {};
    
    // Define main category mappings
    const mainCategories: Record<string, string> = {
      'Exterior': 'Exterior',
      'Interior': 'Interior',
      'Mechanical': 'Mechanical',
      'Safety': 'Safety',
      'Entertainment': 'Technology',
      'Comfort': 'Comfort & Convenience',
      'Convenience': 'Comfort & Convenience',
      'Tech': 'Technology',
      'Technology': 'Technology',
      'Security': 'Safety',
      'Driver Assistance': 'Safety',
      'ADAS': 'Safety',
      // More mappings can be added
    };
    
    Object.entries(equipment).forEach(([category, items]) => {
      // Find the main category or use "Other" as default
      let mainCategory = "Other";
      
      for (const [key, value] of Object.entries(mainCategories)) {
        if (category.toLowerCase().includes(key.toLowerCase())) {
          mainCategory = value;
          break;
        }
      }
      
      if (!grouped[mainCategory]) {
        grouped[mainCategory] = {};
      }
      
      grouped[mainCategory][category] = items;
    });
    
    return grouped;
  },

  // Format angle names for better readability
  formatAngleName(angle: string): string {
    if (!angle) return "Unknown Angle";
    
    // Convert camelCase or snake_case to spaces
    const formatted = angle
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ')         // Replace underscores with spaces
      .trim();
    
    // Capitalize first letter of each word
    return formatted
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
};

// Export individual functions for direct import if needed
export const getVehicleInfo = DataOneUtils.getVehicleInfo;
export const getAdasSystems = DataOneUtils.getAdasSystems;
export const getEquipmentGroups = DataOneUtils.getEquipmentGroups;
export const getColorOptions = DataOneUtils.getColorOptions;
export const getVehicleImages = DataOneUtils.getVehicleImages;
